/**
 * Pointer/wheel -> pan/zoom event handlers for `world-map-pane.tsx`'s
 * canvas, factored out so the pane's own component function stays short.
 * Not pure (reads/writes DOM `PointerEvent`s and a Solid setter), but the
 * pan/zoom *math* itself is one line per handler, on top of the already-pure
 * `assets/world-map-projection.ts`.
 */

import type { PanZoom } from "./world-map-projection.ts";

const MIN_ZOOM = 0.2;
const MAX_ZOOM = 25;
const ZOOM_STEP = 1.15;
/** Screen-pixel drag distance under which a pointer-up is treated as a click (select), not a pan. */
const CLICK_SLOP_PX = 4;

export interface MapPointerCallbacks {
  readonly getPanZoom: () => PanZoom;
  readonly setPanZoom: (updater: (current: PanZoom) => PanZoom) => void;
  /** Fired on a pointer-up that moved less than `CLICK_SLOP_PX` — client (viewport) coordinates. */
  readonly onClick: (clientX: number, clientY: number) => void;
}

export interface MapPointerHandlers {
  onWheel(event: WheelEvent): void;
  onPointerDown(event: PointerEvent): void;
  onPointerMove(event: PointerEvent): void;
  onPointerUp(event: PointerEvent): void;
}

export function createMapPointerHandlers(callbacks: MapPointerCallbacks): MapPointerHandlers {
  let dragOrigin: { x: number; y: number; panX: number; panY: number } | undefined;
  let dragDistance = 0;

  return {
    onWheel(event) {
      event.preventDefault();
      const factor = event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      callbacks.setPanZoom((current) => ({ ...current, zoom: Math.min(Math.max(current.zoom * factor, MIN_ZOOM), MAX_ZOOM) }));
    },
    onPointerDown(event) {
      const current = callbacks.getPanZoom();
      dragOrigin = { x: event.clientX, y: event.clientY, panX: current.panX, panY: current.panY };
      dragDistance = 0;
      (event.currentTarget as Element).setPointerCapture(event.pointerId);
    },
    onPointerMove(event) {
      if (!dragOrigin) return;
      const dx = event.clientX - dragOrigin.x;
      const dy = event.clientY - dragOrigin.y;
      dragDistance = Math.max(dragDistance, Math.hypot(dx, dy));
      const origin = dragOrigin;
      callbacks.setPanZoom((current) => ({ ...current, panX: origin.panX + dx, panY: origin.panY + dy }));
    },
    onPointerUp(event) {
      (event.currentTarget as Element).releasePointerCapture(event.pointerId);
      const wasClick = dragDistance < CLICK_SLOP_PX;
      dragOrigin = undefined;
      if (wasClick) callbacks.onClick(event.clientX, event.clientY);
    },
  };
}
